import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { API } from "../lib/api";
import { DatePicker } from "../components/DatePicker";

const Auditoria = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    usuario: '',
    acao: ''
  });

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim);
      if (filtros.usuario) params.append('usuario', filtros.usuario);
      if (filtros.acao && filtros.acao !== 'todas') params.append('acao', filtros.acao);

      const response = await axios.get(`${API}/auditoria?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      toast.error("Erro ao carregar logs de auditoria");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const carregar = async () => { await fetchLogs(); };
    carregar();
  }, []);

  const handleFiltrar = () => {
    setLoading(true);
    fetchLogs();
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getAcaoColor = (acao) => {
    switch (acao) {
      case 'CREATE': return 'bg-green-100 dark:bg-green-950/40 text-green-800 dark:text-green-300';
      case 'UPDATE': return 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300';
      case 'DELETE': return 'bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-300';
      default: return 'bg-gray-100 dark:bg-muted text-gray-800 dark:text-gray-200';
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Auditoria</h1>
        <p className="text-gray-600 dark:text-gray-300">Logs de atividades do sistema</p>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="dataInicio">Data Início</Label>
              <DatePicker
                id="dataInicio"
                value={filtros.dataInicio}
                onChange={(value) => setFiltros({...filtros, dataInicio: value})}
                placeholder="Data início"
              />
            </div>
            <div>
              <Label htmlFor="dataFim">Data Fim</Label>
              <DatePicker
                id="dataFim"
                value={filtros.dataFim}
                onChange={(value) => setFiltros({...filtros, dataFim: value})}
                placeholder="Data fim"
                minDate={filtros.dataInicio}
              />
            </div>
            <div>
              <Label htmlFor="usuario">Usuário</Label>
              <Input
                id="usuario"
                value={filtros.usuario}
                onChange={(e) => setFiltros({...filtros, usuario: e.target.value})}
                placeholder="Nome do usuário"
              />
            </div>
            <div>
              <Label htmlFor="acao">Ação</Label>
              <Select value={filtros.acao} onValueChange={(value) => setFiltros({...filtros, acao: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas as ações" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="CREATE">Criação</SelectItem>
                  <SelectItem value="UPDATE">Atualização</SelectItem>
                  <SelectItem value="DELETE">Exclusão</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={handleFiltrar}>
              Filtrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data/Hora</TableHead>
                <TableHead>Usuário</TableHead>
                <TableHead>Ação</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Descrição</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>{formatDateTime(log.timestamp)}</TableCell>
                  <TableCell className="font-medium">{log.user_name}</TableCell>
                  <TableCell>
                    <Badge className={getAcaoColor(log.action)}>
                      {log.action}
                    </Badge>
                  </TableCell>
                  <TableCell>{log.entity_type}</TableCell>
                  <TableCell>{log.entity_id}</TableCell>
                  <TableCell className="max-w-xs truncate">
                    {log.description && (
                      <span title={log.description}>
                        {log.description.length > 50 ? `${log.description.substring(0, 50)}...` : log.description}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {logs.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              Nenhum log encontrado
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Auditoria;
